import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { GOOGLE_DOCS } from '@/lib/docs'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'

/*
 * Settings consolidation, batch 2 (2026-09-15). Both pickers lived on
 * `pages/seo/SearchPerformance`, beside the figures they change. They are opened
 * from Settings › Connections now — the per-site lines on the Search Console
 * and Analytics cards — and Search performance links there.
 */

/**
 * The picker itself. Fetched only when it is opened, because it is a live call to
 * Google — a list loaded on every page view would be a Google round trip a tenant
 * did not ask for.
 *
 * **Unreadable properties are shown and disabled rather than hidden.** An account
 * with `siteUnverifiedUser` on a property can see it listed and gets nothing back
 * from any query against it; hiding it would leave somebody who *knows* they own
 * that property looking at a list without it, certain the product is broken. Saying
 * *"this account can see it but not read it"* is the answer they can act on.
 */
export function PropertyPicker({
  siteId,
  current,
  onChosen,
}: {
  siteId: string
  current: string | null
  onChosen: () => void
}): JSX.Element {
  const properties = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/properties', {
          params: { path: { site_id: siteId } },
          signal,
        }),
      ),
    [siteId],
  )
  const choose = useMutation((siteUrl: string) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/property', {
        params: { path: { site_id: siteId } },
        body: { site_url: siteUrl },
      }),
    ),
  )

  if (properties.state.status === 'loading') return <Loading label="Asking Google" />
  if (properties.state.status === 'error') {
    return <ErrorState failure={properties.state.failure} onRetry={properties.refetch} />
  }

  const listed = properties.state.data.properties
  if (listed.length === 0) {
    return (
      <Empty
        title="This Google account owns no Search Console properties"
        body="Sign in with the account that owns your site, ask whoever manages it to add you in Search Console, or verify the site yourself with one DNS record."
        action={
          <>
            <Link to="/settings/connections">Connect a different account</Link>
            {' · '}
            <a
              className="ar-link"
              href={GOOGLE_DOCS.verifyYourSite}
              target="_blank"
              rel="noreferrer noopener"
            >
              How to verify your site ↗
            </a>
          </>
        }
      />
    )
  }

  return (
    <div className="ar-panel ar-stack ar-stack--tight">
      <InlineError failure={choose.state.failure} />
      <strong>Which property is this site?</strong>
      <p className="ar-text-body-sm ar-muted" style={{ margin: 0 }}>
        A domain property covers every subdomain and both http and https. A URL-prefix property
        covers exactly that address — they hold different data.
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {listed.map((property) => (
          <li key={property.site_url} className="app-conn">
            <span className="app-conn__body">
              <span className="ar-mono">{property.site_url}</span>
              <span className="ar-text-body-sm ar-muted">
                {property.is_domain_property ? 'Domain property' : 'URL prefix'} ·{' '}
                {property.readable
                  ? property.permission_level
                  : 'this account can see it but not read it'}
              </span>
            </span>
            <Button
              size="sm"
              tone={property.site_url === current ? 'ghost' : 'primary'}
              disabled={!property.readable || property.site_url === current}
              busy={choose.pending}
              busyLabel="Saving"
              onClick={() => {
                void (async () => {
                  const result = await choose.run(property.site_url)
                  if (result.ok) onChosen()
                })()
              }}
            >
              {property.site_url === current ? 'Current' : 'Use this'}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}
